import { useCallback, useEffect, useState } from 'react';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { reportUserVisibleFailure } from '../../shared/sentry/sentryUser';
import { useNavigation } from '@react-navigation/native';
import { navigateRoot, navigateInShell } from '../../shell/navigation/rootNavigation';
import { openFiscalDestination } from '../../shell/navigation/fiscalNavigation';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBusinessProfileStore } from '../../shell/session/store/useBusinessProfileStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { fetchUserBranches } from '../../auth/application/authBranches';
import type { StockSalePolicy } from '../../shell/session/settingsRepo';
import {
  updateBranchBarcodeInputMode,
  updateBranchConsolidateCartLines,
  updateBranchIdentity,
  updateBranchKitchenFireMode,
  updateBranchKitchenAutoArchiveOnDelivered,
  updateBranchManualDiscountThresholds,
  updateBranchScaleBarcodeProfile,
  updateBranchStockSalePolicy,
  updateBranchSellableModifierAssumption,
} from '../../auth/infra/branches/branchesRepo';
import type { BranchBarcodeInputMode, BranchScaleBarcodeProfile } from '../../shell/session/store/sessionTypes';
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { showError, showErrorAfterModalClose, showInfo, showSuccess } from '../../shared/ui/appToast';
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService';
import { isPublicStoreFeaturesVisible } from '../../shell/feature-flags/publicStoreFeaturesGate';
import { planAllowsPromotions, setOrganizationEntitlementsCache } from '../../shell/feature-flags/planEntitlementsService';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { getDefaultPosFiscalMode, type PosFiscalDefaultStrategy, type ArcaEnvironment } from '../../fiscal/domain-api';
import { getArcaIntegrationStatus, persistDefaultPosFiscalMode } from '../../fiscal/infra-api';
import { isOperationModeStorefrontOff, parseOperationMode } from '../../shell/session/domain/operationMode';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import {
  patchSessionBranch,
  syncBranchSettingsFromLocalCacheToSession,
} from '../../auth/infra/branches/branchSettingsSessionSync';
import { setConsolidateCartLines } from '../../shared/pos/posCartSettings';
import { canMutateBranchSettings, validateBranchNameForSave } from './branchSettingsScreen.utils';
import { pickProductImage, isProductImageValidationError } from '../../catalog/infra-api';
import { optimizeStoreImage } from '../infra/optimizeStoreImage';
import { requireSessionOrganizationId, getKnownSessionOrganizationId } from '../../shell/session/sessionOrganization';
import {
  emptyStoreProfileDraft,
  isStoreOpenNow,
  isValidSlug,
  normalizeSlug,
  storeProfileToDraft,
  type BusinessHourDay,
  type ManualOverride,
  type ScheduledClosure,
  type StoreProfileDraft,
  type TimeShift,
} from '../domain/storeProfileTypes';
import { normalizeStoreContactFields } from '../domain/storeContactPhone';
import {
  isSlugAvailable,
  removeStoreCover,
  removeStoreLogo,
  updateStoreProfile,
  uploadStoreCover,
  uploadStoreBackground,
  removeStoreBackground,
  uploadStoreLogo,
} from '../infra/storeProfileRepo';
import {
  getCachedStoreProfile,
  getCachedMpIntegrationStatus,
} from '../application/storefrontStatus';
import {
  createScheduledClosure,
  deleteScheduledClosure,
  listScheduledClosures,
} from '../infra/scheduledClosuresRepo';
import { allowsWeightSales } from '../../shell/feature-flags/businessCapabilitiesService';
import { DEFAULT_BRANCH_SCALE_CONFIG } from '../../scale/domain/scaleConfig'
import type { BranchScaleConfig, ScaleProtocol } from '../../scale/domain/scaleConfig'
import { getBranchScaleConfig, setBranchScaleConfig } from '../../scale/infra/scaleConfigRepo';
import { DEFAULT_BRANCH_RECEIPT_PRINT_CONFIG } from '../../printing/domain/printConfig'
import type { BranchReceiptPrintConfig, ReceiptPrintMode } from '../../printing/domain/printConfig'
import { getBranchReceiptPrintConfig, setBranchReceiptPrintConfig } from '../../printing/infra/printConfigRepo';
import type { ReceiptWidthMm } from '@vuelto/print';
import type { MpIntegrationStatus } from '../../mercadopago/infra/mpSettingsRepo';

function storeProfileDirtyKey(draft: StoreProfileDraft): string {
  return JSON.stringify(draft);
}

export function useBranchSettingsScreen() {
  const navigation = useNavigation<any>();
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const branches = useSessionStore((state) => state.branches);
  const setBranches = useSessionStore((state) => state.setBranches);
  const { canView, canEdit } = useBranchPermission('BranchSettings');
  const canEditProfile = useModulePermission('BranchSettings', 'edit_profile');
  const canEditHours = useModulePermission('BranchSettings', 'edit_hours');
  const canPublish = useModulePermission('BranchSettings', 'publish');
  const canEditZones = useModulePermission('BranchSettings', 'edit_zones');
  // Tarifas de delivery requieren edit_zones (RLS UPDATE delivery_zones exige edit_zones).
  // El action edit_fees fue removido del catálogo — fees = edit_zones (#448).
  const canEditFees = canEditZones;
  const canToggleDelivery = useModulePermission('BranchSettings', 'toggle');
  const canEditCommercial = useModulePermission('BranchSettings', 'edit_commercial');
  const canEditFiscal = useModulePermission('BranchSettings', 'edit_fiscal');
  const canEditPosRules = useModulePermission('BranchSettings', 'edit_pos_rules');
  const canManageMp = useModulePermission('BranchSettings', 'manage_mp');
  const canManageFiscal = useModulePermission('BranchSettings', 'manage_fiscal');
  const canManageOtherIntegrations = useModulePermission('BranchSettings', 'manage_other');
  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();
  const session = useAuthStore((state) => state.session);
  const { entitlements, refresh: refreshEntitlements } = usePlanEntitlements();
  const businessProfile = useBusinessProfileStore((s) => s.profile);
  const businessTypeCode = businessProfile?.business_type_code ?? null;
  const storefrontEnabled = !isOperationModeStorefrontOff(parseOperationMode(businessProfile?.business_config));
  const mercadoPagoIntegrationsEnabled = isGlobalFeatureOn(globalFeatureFlags, 'mercadopago_integrations');
  const arcaIntegrationsEnabled = isGlobalFeatureOn(globalFeatureFlags, 'arca_integrations');
  const publicStoreFeaturesEnabled = isPublicStoreFeaturesVisible(
    globalFeatureFlags,
    businessTypeCode,
    storefrontEnabled,
  );
  const promotionsIntegrationsEnabled =
    isGlobalFeatureOn(globalFeatureFlags, 'promotions_integrations') && planAllowsPromotions(entitlements);
  const scaleIntegrationsEnabled =
    allowsWeightSales(businessProfile) && isGlobalFeatureOn(globalFeatureFlags, 'scale_integration');
  const isOwner = entitlements?.is_owner === true;
  const allowEditCommercial = isOwner || canEditCommercial;
  const allowEditPosRules = isOwner || canEditPosRules;
  const allowManageMp = isOwner || canManageMp;
  const allowManageFiscal = isOwner || canManageFiscal;
  const allowManageOtherIntegrations = isOwner || canManageOtherIntegrations;
  const allowViewIntegrations = isOwner || canView;
  const currentBranch = branches.find((branch) => branch.id === currentBranchId) ?? null;

  const [loading, setLoading] = useState(() => currentBranchId != null && currentBranch == null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [branchIdentityEditOpen, setBranchIdentityEditOpen] = useState(false);
  const [branchNameInput, setBranchNameInput] = useState('');
  const [branchAddressInput, setBranchAddressInput] = useState('');
  const [branchIdentitySaving, setBranchIdentitySaving] = useState(false);
  const [branchPolicySaving, setBranchPolicySaving] = useState(false);
  const [savingDefaultPosFiscal, setSavingDefaultPosFiscal] = useState(false);

  useEffect(() => {
    if (!currentBranch) {
      setBranchNameInput('');
      setBranchAddressInput('');
      setBranchIdentityEditOpen(false);
      return;
    }

    if (!branchIdentityEditOpen) {
      setBranchNameInput(currentBranch.name ?? '');
      setBranchAddressInput(currentBranch.location ?? '');
    }
  }, [currentBranch, branchIdentityEditOpen]);

  const stockSalePolicy = currentBranch?.stockSalePolicy ?? 'block';
  const sellableModifierAssumption = currentBranch?.sellableModifierAssumption ?? 'base';
  const barcodeInputMode = currentBranch?.barcodeInputMode ?? 'both';
  const scaleBarcodeProfile = currentBranch?.scaleBarcodeProfile ?? 'gs1_prefix';
  const consolidateCartLines = currentBranch?.consolidateCartLines ?? true;
  const kitchenFireMode = currentBranch?.kitchenFireMode ?? 'after_payment';
  const kitchenAutoArchiveOnDelivered = currentBranch?.kitchenAutoArchiveOnDelivered !== false;
  const manualDiscountMaxPercent = currentBranch?.manualDiscountMaxPercent ?? 20;
  const manualDiscountMaxAmount = currentBranch?.manualDiscountMaxAmount ?? 5000;
  const [manualDiscountPercentInput, setManualDiscountPercentInput] = useState(String(manualDiscountMaxPercent));
  const [manualDiscountAmountInput, setManualDiscountAmountInput] = useState(String(manualDiscountMaxAmount));
  const [manualDiscountSaving, setManualDiscountSaving] = useState(false);
  const [supervisorPinConfigured, setSupervisorPinConfigured] = useState(false);
  const [supervisorPinInput, setSupervisorPinInput] = useState('');
  const [supervisorPinSaving, setSupervisorPinSaving] = useState(false);
  const [scaleConfig, setScaleConfig] = useState<BranchScaleConfig>(DEFAULT_BRANCH_SCALE_CONFIG);
  const [scaleComPortInput, setScaleComPortInput] = useState('');
  const [scaleAgentUrlInput, setScaleAgentUrlInput] = useState(DEFAULT_BRANCH_SCALE_CONFIG.agentUrl);
  const [scaleConfigSaving, setScaleConfigSaving] = useState(false);
  const [printConfig, setPrintConfig] = useState<BranchReceiptPrintConfig>(DEFAULT_BRANCH_RECEIPT_PRINT_CONFIG);
  const [printConfigSaving, setPrintConfigSaving] = useState(false);

  const seedSlugFromBranch = useCallback((name?: string | null) => normalizeSlug(name ?? '') || '', []);
  const [storeProfileDraft, setStoreProfileDraft] = useState<StoreProfileDraft>(() => emptyStoreProfileDraft());
  const [storeProfileBaselineKey, setStoreProfileBaselineKey] = useState(() =>
    storeProfileDirtyKey(emptyStoreProfileDraft())
  );
  const [storeProfileLoading, setStoreProfileLoading] = useState(false);
  const [storeProfileSaving, setStoreProfileSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const [closures, setClosures] = useState<ScheduledClosure[]>([]);
  const [closuresLoading, setClosuresLoading] = useState(false);
  const [tcModalVisible, setTcModalVisible] = useState(false);
  const [tcAccepting, setTcAccepting] = useState(false);

  // #483: Real MP OAuth/POS state for the hub — don't confuse permission with connection.
  const [mpIntegrationStatus, setMpIntegrationStatus] = useState<MpIntegrationStatus | null>(null);
  const [mpStatusLoaded, setMpStatusLoaded] = useState(false);
  // Estado real de facturación ARCA (perfil validado), no el feature flag global.
  const [arcaSetupValidated, setArcaSetupValidated] = useState<boolean | null>(null);
  const [arcaEnvironment, setArcaEnvironment] = useState<ArcaEnvironment | null>(null);
  const [arcaPointOfSale, setArcaPointOfSale] = useState<number | null>(null);
  const [arcaPvValidated, setArcaPvValidated] = useState<boolean | null>(null);
  const [arcaStatusLoaded, setArcaStatusLoaded] = useState(false);

  const storeProfileDirty = storeProfileDirtyKey(storeProfileDraft) !== storeProfileBaselineKey;

  useEffect(() => {
    setManualDiscountPercentInput(String(currentBranch?.manualDiscountMaxPercent ?? 20));
    setManualDiscountAmountInput(String(currentBranch?.manualDiscountMaxAmount ?? 5000));
  }, [currentBranch?.manualDiscountMaxAmount, currentBranch?.manualDiscountMaxPercent, currentBranchId]);

  useEffect(() => {
    let alive = true;
    if (!currentBranchId) {
      setSupervisorPinConfigured(false);
      return;
    }
    void import('../../shell/session/supervisorPinApi')
      .then(({ branchHasSupervisorPin }) => branchHasSupervisorPin(currentBranchId))
      .then((ok) => {
        if (alive) setSupervisorPinConfigured(ok);
      })
      .catch(() => {
        if (alive) setSupervisorPinConfigured(false);
      });
    return () => {
      alive = false;
    };
  }, [currentBranchId]);

  useEffect(() => {
    let alive = true;
    if (!currentBranchId || !scaleIntegrationsEnabled) {
      setScaleConfig(DEFAULT_BRANCH_SCALE_CONFIG);
      return;
    }
    void getBranchScaleConfig(currentBranchId).then((cfg) => {
      if (alive) setScaleConfig(cfg);
    });
    return () => {
      alive = false;
    };
  }, [currentBranchId, scaleIntegrationsEnabled]);

  useEffect(() => {
    let alive = true;
    if (!currentBranchId) {
      setPrintConfig(DEFAULT_BRANCH_RECEIPT_PRINT_CONFIG);
      return;
    }
    void getBranchReceiptPrintConfig(currentBranchId).then((cfg) => {
      if (alive) setPrintConfig(cfg);
    });
    return () => {
      alive = false;
    };
  }, [currentBranchId]);

  const loadStoreProfile = useCallback(async (branchId: string, branchName?: string | null, opts?: { force?: boolean }) => {
    setStoreProfileLoading(true);
    try {
      const profile = await getCachedStoreProfile(branchId, opts);
      const draft = storeProfileToDraft(profile, seedSlugFromBranch(branchName));
      setStoreProfileDraft(draft);
      setStoreProfileBaselineKey(storeProfileDirtyKey(draft));
    } catch (e: unknown) {
      console.warn('[BranchSettings] store profile load', e);
      const draft = emptyStoreProfileDraft(seedSlugFromBranch(branchName));
      setStoreProfileDraft(draft);
      setStoreProfileBaselineKey(storeProfileDirtyKey(draft));
    } finally {
      setStoreProfileLoading(false);
    }
  }, [seedSlugFromBranch]);

  const loadClosures = useCallback(async (branchId: string) => {
    setClosuresLoading(true);
    try {
      const list = await listScheduledClosures(branchId);
      setClosures(list);
    } catch (e: unknown) {
      console.warn('[BranchSettings] closures load', e);
      setClosures([]);
    } finally {
      setClosuresLoading(false);
    }
  }, []);

  const load = useCallback(async (options?: { force?: boolean }) => {
    if (!currentBranchId) {
      setLoading(false);
      return;
    }
    const hasCachedBranch = Boolean(useSessionStore.getState().branches.find((branch) => branch.id === currentBranchId));
    if (!options?.force && hasCachedBranch) {
      setLoadError(null);
      setLoading(false);
      await syncBranchSettingsFromLocalCacheToSession();
      const branch = useSessionStore.getState().branches.find((b) => b.id === currentBranchId);
      await loadStoreProfile(currentBranchId, branch?.name, { force: options?.force });
      void loadClosures(currentBranchId);
      return;
    }
    const showBlockingLoader = options?.force === true || !hasCachedBranch;
    if (showBlockingLoader) setLoading(true);
    setLoadError(null);
    try {
      const nextBranches = await fetchUserBranches({ force: options?.force });
      setBranches(nextBranches);
      await syncBranchSettingsFromLocalCacheToSession();
      const branch = nextBranches.find((b) => b.id === currentBranchId);
      await loadStoreProfile(currentBranchId, branch?.name, { force: options?.force });
      void loadClosures(currentBranchId);
    } catch (e: unknown) {
      console.warn('[BranchSettings] load', e);
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : 'No se pudo cargar la configuración de la sucursal.';
      setLoadError(msg || 'No se pudo cargar la configuración de la sucursal.');
    } finally {
      if (showBlockingLoader) setLoading(false);
    }
  }, [currentBranchId, loadStoreProfile, setBranches]);

  useEffect(() => {
    if (!currentBranchId) {
      setStoreProfileDraft(emptyStoreProfileDraft());
      return;
    }
    void loadStoreProfile(currentBranchId, currentBranch?.name);
    void loadClosures(currentBranchId);
  }, [currentBranchId]); // eslint-disable-line react-hooks/exhaustive-deps -- reload only on branch switch

  // #483: Fetch real MP OAuth/POS state so the hub shows connection, not permission.
  useEffect(() => {
    let alive = true;
    if (!currentBranchId || !mercadoPagoIntegrationsEnabled) {
      setMpIntegrationStatus(null);
      setMpStatusLoaded(false);
      return;
    }
    setMpStatusLoaded(false);
    void getCachedMpIntegrationStatus(currentBranchId)
      .then((status) => {
        if (alive) setMpIntegrationStatus(status);
      })
      .catch(() => {
        if (alive) setMpIntegrationStatus(null);
      })
      .finally(() => {
        if (alive) setMpStatusLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [currentBranchId, mercadoPagoIntegrationsEnabled]);

  // Estado real ARCA para el hub. El wizard invalida el cache al conectar;
  // Integraciones tiene que volver a leerlo al enfocar, porque esta pantalla queda montada.
  const refreshArcaIntegrationStatus = useCallback(async (options?: { force?: boolean }) => {
    if (!currentBranchId || !arcaIntegrationsEnabled) {
      setArcaSetupValidated(null);
      setArcaEnvironment(null);
      setArcaPointOfSale(null);
      setArcaPvValidated(null);
      setArcaStatusLoaded(false);
      return;
    }
    try {
      const status = await getArcaIntegrationStatus(currentBranchId, {
        force: options?.force === true,
      });
      setArcaSetupValidated(status.setupStatus === 'validated');
      setArcaEnvironment(status.environment);
      setArcaPointOfSale(status.pointOfSale);
      setArcaPvValidated(status.pvValidated);
    } catch {
      setArcaSetupValidated(false);
      setArcaPointOfSale(null);
      setArcaPvValidated(null);
    } finally {
      setArcaStatusLoaded(true);
    }
  }, [arcaIntegrationsEnabled, currentBranchId]);

  useEffect(() => {
    void refreshArcaIntegrationStatus();
  }, [refreshArcaIntegrationStatus]);


  const retryLoad = useCallback(async () => {
    await load({ force: true });
  }, [load]);

  const handleOpenArcaIntegration = () => {
    openFiscalDestination(navigation, {
      currentBranchId,
      arcaIntegrationsEnabled,
    });
  };

  const handleOpenMercadoPago = () => {
    navigateInShell(navigation, 'MercadoPagoBranchSettings');
  };

  const handleOpenBranchSelector = () => {
    navigateRoot(navigation, 'BranchSelector');
  };

  const openManageBranches = () => {
    navigateRoot(navigation, 'ManageBranchesOverlay');
  };

  const openBranchIdentityEdit = () => {
    if (!allowEditCommercial) return;
    setBranchNameInput(currentBranch?.name ?? '');
    setBranchIdentityEditOpen(true);
  };

  const closeBranchIdentityEdit = () => {
    setBranchNameInput(currentBranch?.name ?? '');
    setBranchIdentityEditOpen(false);
  };

  const handleSaveBranchIdentity = async () => {
    if (!canMutateBranchSettings(allowEditCommercial, currentBranchId)) return;
    const parsedName = validateBranchNameForSave(branchNameInput);
    if (!parsedName.ok) {
      showError('Nombre requerido', 'Ingresá un nombre para la sucursal.');
      return;
    }
    const nextName = parsedName.value;

    try {
      setBranchIdentitySaving(true);
      await updateBranchIdentity(currentBranchId!, {
        name: nextName,
      });
      patchSessionBranch(currentBranchId!, {
        name: nextName,
      });
      setBranchIdentityEditOpen(false);
      void logBusinessAuditEvent('branch.settings.identity', { branchId: currentBranchId });
      showSuccess('Guardado', 'Sucursal actualizada.');
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar el nombre.' });
    } finally {
      setBranchIdentitySaving(false);
    }
  };

  const handleSaveStockPolicy = async (policy: StockSalePolicy) => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || branchPolicySaving) return;
    if (policy === stockSalePolicy) return;
    setBranchPolicySaving(true);
    try {
      await updateBranchStockSalePolicy(currentBranchId!, policy);
      patchSessionBranch(currentBranchId!, { stockSalePolicy: policy });
      void logBusinessAuditEvent('branch.settings.stock_policy', {
        branchId: currentBranchId ?? undefined,
        policy,
      });
      showSuccess('Venta sin stock actualizada');
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar.' });
    } finally {
      setBranchPolicySaving(false);
    }
  };

  const handleSaveSellableModifierAssumption = async (assumption: 'base' | 'worst_case') => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || branchPolicySaving) return;
    if (assumption === sellableModifierAssumption) return;
    setBranchPolicySaving(true);
    try {
      await updateBranchSellableModifierAssumption(currentBranchId!, assumption);
      patchSessionBranch(currentBranchId!, { sellableModifierAssumption: assumption });
      void logBusinessAuditEvent('branch.settings.sellable_modifier_assumption', {
        branchId: currentBranchId ?? undefined,
        assumption,
      });
      showSuccess('Vendible de combos actualizado');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar.' });
    } finally {
      setBranchPolicySaving(false);
    }
  };

  const handleSaveBarcodeInputMode = async (mode: BranchBarcodeInputMode) => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || branchPolicySaving) return;
    if (mode === barcodeInputMode) return;
    setBranchPolicySaving(true);
    try {
      await updateBranchBarcodeInputMode(currentBranchId!, mode);
      patchSessionBranch(currentBranchId!, { barcodeInputMode: mode });
      void logBusinessAuditEvent('branch.settings.barcode_input', { branchId: currentBranchId, mode });
      showSuccess('Lectura de códigos actualizada');
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar.' });
    } finally {
      setBranchPolicySaving(false);
    }
  };

  const handleSaveScaleBarcodeProfile = async (profile: BranchScaleBarcodeProfile) => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || branchPolicySaving) return;
    if (profile === scaleBarcodeProfile) return;
    setBranchPolicySaving(true);
    try {
      await updateBranchScaleBarcodeProfile(currentBranchId!, profile);
      patchSessionBranch(currentBranchId!, { scaleBarcodeProfile: profile });
      void logBusinessAuditEvent('branch.settings.scale_barcode', { branchId: currentBranchId, profile });
      showSuccess('Ticket de balanza actualizado');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar.' });
    } finally {
      setBranchPolicySaving(false);
    }
  };

  const handleSaveConsolidateCartLines = async (enabled: boolean) => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || branchPolicySaving) return;
    if (enabled === consolidateCartLines) return;
    setBranchPolicySaving(true);
    try {
      await updateBranchConsolidateCartLines(currentBranchId!, enabled);
      patchSessionBranch(currentBranchId!, { consolidateCartLines: enabled });
      setConsolidateCartLines(enabled);
      void logBusinessAuditEvent('branch.settings.consolidate_cart_lines', { branchId: currentBranchId, enabled });
      showSuccess(enabled ? 'Items repetidos se juntan' : 'Cada agregado va aparte');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar.' });
    } finally {
      setBranchPolicySaving(false);
    }
  };

  const handleSaveKitchenFireMode = async (mode: 'after_payment' | 'before_payment') => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || branchPolicySaving) return;
    if (mode === kitchenFireMode) return;
    setBranchPolicySaving(true);
    try {
      await updateBranchKitchenFireMode(currentBranchId!, mode);
      patchSessionBranch(currentBranchId!, { kitchenFireMode: mode });
      void logBusinessAuditEvent('branch.settings.kitchen_fire_mode', { branchId: currentBranchId, mode });
      showSuccess(mode === 'before_payment' ? 'Cocina antes de cobrar' : 'Cocina después de cobrar');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar.' });
    } finally {
      setBranchPolicySaving(false);
    }
  };

  const handleSaveKitchenAutoArchiveOnDelivered = async (enabled: boolean) => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || branchPolicySaving) return;
    if (enabled === kitchenAutoArchiveOnDelivered) return;
    setBranchPolicySaving(true);
    try {
      await updateBranchKitchenAutoArchiveOnDelivered(currentBranchId!, enabled);
      patchSessionBranch(currentBranchId!, { kitchenAutoArchiveOnDelivered: enabled });
      void logBusinessAuditEvent('branch.settings.kitchen_auto_archive', {
        branchId: currentBranchId,
        enabled,
      });
      showSuccess(enabled ? 'Al entregar, archivamos la comanda' : 'La comanda queda en listo al entregar');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar.' });
    } finally {
      setBranchPolicySaving(false);
    }
  };

  const handleSaveScaleConfig = async (patch: Partial<BranchScaleConfig>, opts?: { silent?: boolean }) => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || scaleConfigSaving) return;
    setScaleConfigSaving(true);
    try {
      const next = await setBranchScaleConfig(currentBranchId!, patch);
      setScaleConfig(next);
      if (!opts?.silent) showSuccess('Balanza actualizada');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'scale_config.save', error: e, userMessage: 'No se pudo guardar la configuración de balanza.' });
    } finally {
      setScaleConfigSaving(false);
    }
  };

  const handleScaleProtocolChange = (protocol: ScaleProtocol) => {
    void handleSaveScaleConfig({ protocol });
  };

  const handleSavePrintConfig = async (patch: Partial<BranchReceiptPrintConfig>) => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || printConfigSaving) return;
    setPrintConfigSaving(true);
    try {
      const next = await setBranchReceiptPrintConfig(currentBranchId!, patch);
      setPrintConfig(next);
      showSuccess('Impresión actualizada');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'print_config.save', error: e, userMessage: 'No se pudo guardar la configuración de impresión.' });
    } finally {
      setPrintConfigSaving(false);
    }
  };

  const fiscalOptionInPlan = entitlements?.fiscal_enabled === true;
  const defaultPosFiscalMode = getDefaultPosFiscalMode(entitlements, fiscalOptionInPlan);

  const handlePersistDefaultPosFiscalMode = async (mode: PosFiscalDefaultStrategy) => {
    const organizationId = entitlements?.organization_id ?? getKnownSessionOrganizationId();
    if (!organizationId || !session?.user?.id) {
      showInfo('Sin organización', 'No encontramos la organización.');
      return;
    }
    if (!isOwner) return;
    if (mode !== 'non_fiscal' && !fiscalOptionInPlan) return;
    if (defaultPosFiscalMode === mode) return;

    try {
      setSavingDefaultPosFiscal(true);
      await persistDefaultPosFiscalMode({
        organizationId,
        userId: session.user.id,
        mode,
        currentEntitlements: entitlements ?? null,
      });
      if (entitlements) {
        setOrganizationEntitlementsCache({
          ...entitlements,
          default_pos_fiscal_mode: mode,
        });
      }
      await refreshEntitlements({ force: true });
      showSuccess('Guardado', 'Cobro en POS actualizado.');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'default_pos_fiscal.save', error: e, userMessage: 'No se pudo guardar la preferencia.' });
    } finally {
      setSavingDefaultPosFiscal(false);
    }
  };

  const handleSaveManualDiscountThresholds = async () => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || manualDiscountSaving) return;
    const percent = Number(manualDiscountPercentInput.replace(',', '.'));
    const amount = Number(manualDiscountAmountInput.replace(',', '.'));
    if (!Number.isFinite(percent) || percent <= 0) {
      showError('Valor inválido', 'Ingresá un tope de porcentaje mayor a 0.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      showError('Valor inválido', 'Ingresá un tope en pesos mayor a 0.');
      return;
    }
    if (percent === manualDiscountMaxPercent && amount === manualDiscountMaxAmount) return;

    setManualDiscountSaving(true);
    try {
      await updateBranchManualDiscountThresholds(currentBranchId!, {
        manualDiscountMaxPercent: percent,
        manualDiscountMaxAmount: amount,
      });
      patchSessionBranch(currentBranchId!, {
        manualDiscountMaxPercent: percent,
        manualDiscountMaxAmount: amount,
      });
      void logBusinessAuditEvent('branch.settings.manual_discount_threshold', {
        branchId: currentBranchId,
        manualDiscountMaxPercent: percent,
        manualDiscountMaxAmount: amount,
      });
      showSuccess('Topes guardados');
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar.' });
    } finally {
      setManualDiscountSaving(false);
    }
  };

  const handleSaveSupervisorPin = async () => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || supervisorPinSaving) return;
    const pin = supervisorPinInput.trim();
    if (pin.length < 4 || pin.length > 12 || !/^\d+$/.test(pin)) {
      showError('PIN inválido', 'Usá solo dígitos, entre 4 y 12.');
      return;
    }
    setSupervisorPinSaving(true);
    try {
      const { setBranchSupervisorPin } = await import('../../shell/session/supervisorPinApi');
      await setBranchSupervisorPin(currentBranchId!, pin);
      setSupervisorPinConfigured(true);
      setSupervisorPinInput('');
      showSuccess('PIN guardado');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar el PIN.' });
    } finally {
      setSupervisorPinSaving(false);
    }
  };

  const handleClearSupervisorPin = async () => {
    if (!canMutateBranchSettings(allowEditPosRules, currentBranchId) || supervisorPinSaving) return;
    setSupervisorPinSaving(true);
    try {
      const { clearBranchSupervisorPin } = await import('../../shell/session/supervisorPinApi');
      await clearBranchSupervisorPin(currentBranchId!);
      setSupervisorPinConfigured(false);
      setSupervisorPinInput('');
      showSuccess('PIN eliminado');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo quitar el PIN.' });
    } finally {
      setSupervisorPinSaving(false);
    }
  };

  const patchStoreProfileDraft = useCallback((patch: Partial<StoreProfileDraft>) => {
    setStoreProfileDraft((prev) => {
      const next = { ...prev, ...patch };
      if ('whatsapp' in patch && !String(patch.whatsapp ?? '').trim()) {
        next.orderContactWhatsapp = false;
      }
      if ('phone' in patch && !String(patch.phone ?? '').trim()) {
        next.orderContactPhone = false;
      }
      return next;
    });
  }, []);

  const handleBusinessHourChange = useCallback((dayOfWeek: number, patch: Partial<BusinessHourDay>) => {
    setStoreProfileDraft((prev) => ({
      ...prev,
      businessHours: prev.businessHours.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d)),
    }));
  }, []);

  const handleAddShift = useCallback((dayOfWeek: number) => {
    setStoreProfileDraft((prev) => ({
      ...prev,
      businessHours: prev.businessHours.map((d) => {
        if (d.dayOfWeek !== dayOfWeek) return d;
        if (d.shifts.length >= 3) return d;
        return { ...d, shifts: [...d.shifts, { open: '09:00', close: '13:00' }] };
      }),
    }));
  }, []);

  const handleRemoveShift = useCallback((dayOfWeek: number, index: number) => {
    setStoreProfileDraft((prev) => ({
      ...prev,
      businessHours: prev.businessHours.map((d) => {
        if (d.dayOfWeek !== dayOfWeek) return d;
        const shifts = d.shifts.filter((_, i) => i !== index);
        return { ...d, shifts, isOpen: shifts.length > 0 && d.isOpen };
      }),
    }));
  }, []);

  const handleShiftChange = useCallback((dayOfWeek: number, index: number, field: keyof TimeShift, value: string) => {
    setStoreProfileDraft((prev) => ({
      ...prev,
      businessHours: prev.businessHours.map((d) => {
        if (d.dayOfWeek !== dayOfWeek) return d;
        return {
          ...d,
          shifts: d.shifts.map((s, i) => (i === index ? { ...s, [field]: value.slice(0, 5) } : s)),
        };
      }),
    }));
  }, []);

  const handleManualOverrideChange = useCallback(async (mode: ManualOverride, durationMinutes?: number) => {
    if (!canMutateBranchSettings(canEditHours, currentBranchId)) return;
    const until = mode !== 'auto' && durationMinutes
      ? new Date(Date.now() + durationMinutes * 60_000).toISOString()
      : null;
    const previousDraft = storeProfileDraft;
    const nextDraft = { ...storeProfileDraft, manualOverride: mode, manualOverrideUntil: until };
    setStoreProfileDraft(nextDraft);
    setStoreProfileBaselineKey(storeProfileDirtyKey(nextDraft));
    try {
      await updateStoreProfile(currentBranchId!, nextDraft);
      void logBusinessAuditEvent('branch.settings.manual_override', {
        branchId: currentBranchId,
        mode,
        until,
      });
      if (mode === 'auto') {
        showSuccess('Horario restaurado', 'La tienda vuelve a seguir el horario normal.');
      } else if (mode === 'force_open') {
        showSuccess('Tienda abierta', 'Los clientes la ven abierta hasta que vuelvas al horario.');
      } else if (durationMinutes) {
        showSuccess('Tienda cerrada', `Cerrada por ${durationMinutes === 60 ? '1 hora' : '3 horas'}.`);
      } else {
        showSuccess('Tienda cerrada', 'Los clientes la ven cerrada hasta que vuelvas al horario.');
      }
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo cambiar el estado de apertura.' });
      setStoreProfileDraft(previousDraft);
      setStoreProfileBaselineKey(storeProfileDirtyKey(previousDraft));
    }
  }, [canEditHours, currentBranchId, storeProfileDraft]);

  const handleDeactivatePublicStore = useCallback(async () => {
    if (!canMutateBranchSettings(canPublish, currentBranchId)) return;
    const previousDraft = storeProfileDraft;
    const nextDraft = { ...storeProfileDraft, isPublic: false };
    setStoreProfileDraft(nextDraft);
    setStoreProfileBaselineKey(storeProfileDirtyKey(nextDraft));
    try {
      await updateStoreProfile(currentBranchId!, nextDraft);
      void logBusinessAuditEvent('branch.settings.store_profile_deactivate', {
        branchId: currentBranchId,
      });
      showSuccess('Tienda desactivada', 'Ya no aparece en el portal ni por el link.');
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo desactivar la tienda.' });
      setStoreProfileDraft(previousDraft);
      setStoreProfileBaselineKey(storeProfileDirtyKey(previousDraft));
    }
  }, [canPublish, currentBranchId, storeProfileDraft]);

  const handleCreateClosure = useCallback(async (startDate: string, endDate: string, reason?: string) => {
    if (!canMutateBranchSettings(canEditHours, currentBranchId)) return;
    if (!currentBranchId) return;
    try {
      await createScheduledClosure({ branchId: currentBranchId, startDate, endDate, reason });
      void logBusinessAuditEvent('branch.settings.closure_create', {
        branchId: currentBranchId,
        startDate,
        endDate,
      });
      showSuccess('Cierre programado', 'Se agregó el cierre.');
      await loadClosures(currentBranchId);
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo crear el cierre programado.' });
    }
  }, [canEditHours, currentBranchId, loadClosures]);

  const handleDeleteClosure = useCallback(async (id: string) => {
    if (!canMutateBranchSettings(canEditHours, currentBranchId)) return;
    if (!currentBranchId) return;
    try {
      await deleteScheduledClosure(id, currentBranchId);
      void logBusinessAuditEvent('branch.settings.closure_delete', {
        branchId: currentBranchId,
        closureId: id,
      });
      await loadClosures(currentBranchId);
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo eliminar el cierre.' });
    }
  }, [canEditHours, currentBranchId, loadClosures]);

  const handleUploadLogo = useCallback(async () => {
    if (!canMutateBranchSettings(canEditProfile, currentBranchId) || logoUploading) return;
    try {
      setLogoUploading(true);
      const picked = await pickProductImage();
      if (!picked) return;
      const optimized = await optimizeStoreImage(picked.uri, 'logo');
      const orgId = await requireSessionOrganizationId();
      const logoUrl = await uploadStoreLogo({
        orgId,
        branchId: currentBranchId!,
        base64: optimized.base64,
        mimeType: optimized.mimeType,
      });
      setStoreProfileDraft((prev) => ({ ...prev, logoUrl }));
      showSuccess('Logo listo', 'Guardá el perfil para publicarlo.');
    } catch (e: any) {
      if (isProductImageValidationError(e)) {
        showError('Imagen no válida', e.message);
      } else {
        presentUnexpectedFailure({ module: 'settings', op: 'no-se-pudo-subir-el-logo', error: e, title: 'No se pudo subir el logo', userMessage: 'Error al subir el logo.' });
      }
    } finally {
      setLogoUploading(false);
    }
  }, [canEditProfile, currentBranchId, logoUploading]);

  const handleRemoveLogo = useCallback(async () => {
    if (!canMutateBranchSettings(canEditProfile, currentBranchId)) return;
    try {
      const orgId = await requireSessionOrganizationId();
      await removeStoreLogo({ orgId, branchId: currentBranchId! });
      setStoreProfileDraft((prev) => ({ ...prev, logoUrl: '' }));
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo quitar el logo.' });
    }
  }, [canEditProfile, currentBranchId]);

  const handleUploadCover = useCallback(async () => {
    if (!canMutateBranchSettings(canEditProfile, currentBranchId) || coverUploading) return;
    try {
      setCoverUploading(true);
      const picked = await pickProductImage();
      if (!picked) return;
      const optimized = await optimizeStoreImage(picked.uri, 'cover');
      const orgId = await requireSessionOrganizationId();
      const coverUrl = await uploadStoreCover({
        orgId,
        branchId: currentBranchId!,
        base64: optimized.base64,
        mimeType: optimized.mimeType,
      });
      setStoreProfileDraft((prev) => ({ ...prev, coverStoragePath: coverUrl, coverFocalX: 50, coverFocalY: 50 }));
      showSuccess('Portada lista', 'Guardá el perfil para publicarla.');
    } catch (e: any) {
      if (isProductImageValidationError(e)) {
        showError('Imagen no válida', e.message);
      } else {
        presentUnexpectedFailure({ module: 'settings', op: 'no-se-pudo-subir-la-portada', error: e, title: 'No se pudo subir la portada', userMessage: 'Error al subir la portada.' });
      }
    } finally {
      setCoverUploading(false);
    }
  }, [canEditProfile, currentBranchId, coverUploading]);

  const handleRemoveCover = useCallback(async () => {
    if (!canMutateBranchSettings(canEditProfile, currentBranchId)) return;
    try {
      const orgId = await requireSessionOrganizationId();
      await removeStoreCover({ orgId, branchId: currentBranchId! });
      setStoreProfileDraft((prev) => ({ ...prev, coverStoragePath: '' }));
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo quitar la portada.' });
    }
  }, [canEditProfile, currentBranchId]);

  // Fondo opcional de la tienda — el portal lo muestra full-bleed con scrim oscuro.
  const handleUploadBackground = useCallback(async () => {
    if (!canMutateBranchSettings(canEditProfile, currentBranchId) || backgroundUploading) return;
    try {
      setBackgroundUploading(true);
      const picked = await pickProductImage();
      if (!picked) return;
      const optimized = await optimizeStoreImage(picked.uri, 'cover');
      const orgId = await requireSessionOrganizationId();
      const bgUrl = await uploadStoreBackground({
        orgId,
        branchId: currentBranchId!,
        base64: optimized.base64,
        mimeType: optimized.mimeType,
      });
      setStoreProfileDraft((prev) => ({ ...prev, backgroundStoragePath: bgUrl, storeTheme: 'dark' }));
      showSuccess('Fondo listo', 'Activamos el tema oscuro. Guardá el perfil para publicarlo.');
    } catch (e: any) {
      if (isProductImageValidationError(e)) {
        showError('Imagen no válida', e.message);
      } else {
        presentUnexpectedFailure({ module: 'settings', op: 'no-se-pudo-subir-el-fondo', error: e, title: 'No se pudo subir el fondo', userMessage: 'Error al subir el fondo.' });
      }
    } finally {
      setBackgroundUploading(false);
    }
  }, [canEditProfile, currentBranchId, backgroundUploading]);

  const handleRemoveBackground = useCallback(async () => {
    if (!canMutateBranchSettings(canEditProfile, currentBranchId)) return;
    try {
      const orgId = await requireSessionOrganizationId();
      await removeStoreBackground({ orgId, branchId: currentBranchId! });
      setStoreProfileDraft((prev) => ({ ...prev, backgroundStoragePath: '' }));
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo quitar el fondo.' });
    }
  }, [canEditProfile, currentBranchId]);

  const handleAcceptTc = useCallback(async () => {
    if (!canMutateBranchSettings(canPublish, currentBranchId) || tcAccepting) return;
    const now = new Date().toISOString();
    const nextDraft = { ...storeProfileDraft, tcAcceptedAt: now, isPublic: true };
    setTcAccepting(true);
    try {
      await updateStoreProfile(currentBranchId!, nextDraft);
      setStoreProfileDraft(nextDraft);
      setStoreProfileBaselineKey(storeProfileDirtyKey(nextDraft));
      setTcModalVisible(false);
      void logBusinessAuditEvent('branch.settings.tc_accept', { branchId: currentBranchId });
      showSuccess('Tienda online', 'Activaste la tienda en el portal.');
    } catch (e: unknown) {
      // Cerrar el modal primero: si el toast sale con el Modal abierto, en web a veces no auto-oculta.
      setTcModalVisible(false);
      showErrorAfterModalClose(
        'No se pudo activar la tienda',
        'Error al activar la tienda online.',
      );
      reportUserVisibleFailure({ module: 'settings', op: 'accept_tc.activate_store', error: e, title: 'No se pudo activar la tienda' });
    } finally {
      setTcAccepting(false);
    }
  }, [canPublish, currentBranchId, storeProfileDraft, tcAccepting]);

  const checkSlugAvailable = useCallback(
    (slug: string) => isSlugAvailable(slug, currentBranchId),
    [currentBranchId],
  );

  const handleSaveStoreProfile = useCallback(async () => {
    if (!canMutateBranchSettings(canEditProfile || canEditHours || canPublish, currentBranchId) || storeProfileSaving) return;
    const slug = normalizeSlug(storeProfileDraft.slug);
    if (!isValidSlug(slug)) {
      showError('Slug inválido', 'Usá solo minúsculas, números y guiones (2–64 caracteres).');
      return;
    }
    const contacts = normalizeStoreContactFields(storeProfileDraft.phone, storeProfileDraft.whatsapp);
    if (contacts.phoneError) {
      showError('Teléfono', contacts.phoneError);
      return;
    }
    if (contacts.whatsappError) {
      showError('WhatsApp', contacts.whatsappError);
      return;
    }
    if (storeProfileDraft.isPublic) {
      try {
        const available = await isSlugAvailable(slug, currentBranchId);
        if (!available) {
          showError('Slug ocupado', 'Elegí otro slug; ese ya está en uso.');
          return;
        }
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'settings', op: 'sin-conexi-n', error: e, title: 'Sin conexión', userMessage: 'Necesitás conexión para verificar el slug.' });
        return;
      }
    }

    setStoreProfileSaving(true);
    try {
      const nextDraft = {
        ...storeProfileDraft,
        slug,
        phone: contacts.phone,
        whatsapp: contacts.whatsapp,
        orderContactWhatsapp: contacts.whatsapp ? storeProfileDraft.orderContactWhatsapp : false,
        orderContactPhone: contacts.phone ? storeProfileDraft.orderContactPhone : false,
      };
      await updateStoreProfile(currentBranchId!, nextDraft);
      setStoreProfileDraft(nextDraft);
      setStoreProfileBaselineKey(storeProfileDirtyKey(nextDraft));
      void logBusinessAuditEvent('branch.settings.store_profile', {
        branchId: currentBranchId,
        slug,
        isPublic: nextDraft.isPublic,
      });
      showSuccess('Guardado', 'Perfil público actualizado.');
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar el perfil público.' });
    } finally {
      setStoreProfileSaving(false);
    }
  }, [canEditProfile, canEditHours, canPublish, currentBranchId, storeProfileDraft, storeProfileSaving]);

  return {
    currentBranchId,
    orgId: getKnownSessionOrganizationId() ?? undefined,
    canView,
    canEdit,
    canEditProfile,
    canEditHours,
    canPublish,
    canEditZones,
    canEditFees,
    canToggleDelivery,
    canEditCommercial: allowEditCommercial,
    canEditFiscal: isOwner || canEditFiscal,
    canEditPosRules: allowEditPosRules,
    canManageMp: allowManageMp,
    canManageFiscal: allowManageFiscal,
    canManageOtherIntegrations: allowManageOtherIntegrations,
    canViewIntegrations: allowViewIntegrations,
    isOwner,
    posFiscalSettingsReady: Boolean(entitlements),
    loading,
    loadError,
    load,
    retryLoad,
    currentBranch,
    branchIdentityEditOpen,
    branchNameInput,
    setBranchNameInput,
    branchAddressInput,
    setBranchAddressInput,
    branchIdentitySaving,
    branchPolicySaving,
    openBranchIdentityEdit,
    closeBranchIdentityEdit,
    handleSaveBranchIdentity,
    mercadoPagoIntegrationsEnabled,
    arcaIntegrationsEnabled,
    promotionsIntegrationsEnabled,
    scaleIntegrationsEnabled,
    scaleConfig,
    scaleConfigSaving,
    handleSaveScaleConfig,
    handleScaleProtocolChange,
    printConfig,
    printConfigSaving,
    handleSavePrintConfig,
    handleOpenArcaIntegration,
    handleOpenMercadoPago,
    mpOAuthConnected: mpIntegrationStatus?.oauth_connected ?? false,
    mpPosConfigured: mpIntegrationStatus?.pos_configured ?? false,
    mpStatusLoaded: !mercadoPagoIntegrationsEnabled || mpStatusLoaded,
    arcaSetupValidated,
    arcaEnvironment,
    arcaPointOfSale,
    arcaPvValidated,
    refreshArcaIntegrationStatus,
    arcaStatusLoaded: !arcaIntegrationsEnabled || arcaStatusLoaded,
    stockSalePolicy,
    handleSaveStockPolicy,
    sellableModifierAssumption,
    handleSaveSellableModifierAssumption,
    barcodeInputMode,
    handleSaveBarcodeInputMode,
    scaleBarcodeProfile,
    handleSaveScaleBarcodeProfile,
    consolidateCartLines,
    handleSaveConsolidateCartLines,
    kitchenFireMode,
    handleSaveKitchenFireMode,
    kitchenAutoArchiveOnDelivered,
    handleSaveKitchenAutoArchiveOnDelivered,
    fiscalOptionInPlan,
    defaultPosFiscalMode,
    savingDefaultPosFiscal,
    handlePersistDefaultPosFiscalMode,
    manualDiscountPercentInput,
    setManualDiscountPercentInput,
    manualDiscountAmountInput,
    setManualDiscountAmountInput,
    manualDiscountSaving,
    handleSaveManualDiscountThresholds,
    supervisorPinConfigured,
    supervisorPinInput,
    setSupervisorPinInput,
    supervisorPinSaving,
    handleSaveSupervisorPin,
    handleClearSupervisorPin,
    handleOpenBranchSelector,
    openManageBranches: isOwner ? openManageBranches : undefined,
    storeProfileDraft,
    storeProfileDirty,
    storeProfileLoading,
    storeProfileSaving,
    checkSlugAvailable,
    logoUploading,
    coverUploading,
    backgroundUploading,
    patchStoreProfileDraft,
    handleBusinessHourChange,
    handleAddShift,
    handleRemoveShift,
    handleShiftChange,
    handleManualOverrideChange,
    handleDeactivatePublicStore,
    handleUploadLogo,
    handleRemoveLogo,
    handleUploadCover,
    handleRemoveCover,
    handleUploadBackground,
    handleRemoveBackground,
    handleSaveStoreProfile,
    tcModalVisible,
    setTcModalVisible,
    tcAccepting,
    handleAcceptTc,
    closures,
    closuresLoading,
    handleCreateClosure,
    handleDeleteClosure,
    isStoreOpenNow,
    publicStoreFeaturesEnabled,
    businessTypeCode,
  };
}

export type BranchSettingsScreenViewModel = ReturnType<typeof useBranchSettingsScreen>;
